import { supabase } from '../lib/supabase';

export const clientReviewService = {
  // Get all published reviews
  async getAllPublishedReviews() {
    try {
      const { data, error } = await supabase
        ?.from('client_reviews')
        ?.select(`
          *,
          company_settings (
            company_name,
            industry
          )
        `)
        ?.eq('is_published', true)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        companyId: row?.company_id,
        companyName: row?.company_settings?.company_name,
        industry: row?.company_settings?.industry,
        userEmail: row?.user_email,
        starRating: row?.star_rating,
        reviewText: row?.review_text,
        complianceSuccessMetrics: row?.compliance_success_metrics,
        systemUsagePeriodDays: row?.system_usage_period_days,
        isVerified: row?.is_verified,
        isPublished: row?.is_published,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching published reviews:', error);
      throw error;
    }
  },

  // Get review analytics
  async getReviewAnalytics() {
    try {
      const { data: reviews, error } = await supabase
        ?.from('client_reviews')
        ?.select('*')
        ?.eq('is_published', true);

      if (error) throw error;

      const totalReviews = reviews?.length || 0;
      const averageRating = totalReviews > 0
        ? reviews?.reduce((sum, r) => sum + (r?.star_rating || 0), 0) / totalReviews
        : 0;

      // Calculate rating distribution
      const ratingDistribution = {
        5: reviews?.filter(r => r?.star_rating === 5)?.length || 0,
        4: reviews?.filter(r => r?.star_rating === 4)?.length || 0,
        3: reviews?.filter(r => r?.star_rating === 3)?.length || 0,
        2: reviews?.filter(r => r?.star_rating === 2)?.length || 0,
        1: reviews?.filter(r => r?.star_rating === 1)?.length || 0
      };

      // Calculate aggregate compliance metrics
      const aggregateMetrics = reviews?.reduce((acc, review) => {
        const metrics = review?.compliance_success_metrics || {};
        
        if (metrics?.cost_savings_kina) {
          acc.totalCostSavings += parseFloat(metrics?.cost_savings_kina) || 0;
        }
        if (metrics?.compliance_violations_prevented) {
          acc.totalViolationsPrevented += parseInt(metrics?.compliance_violations_prevented) || 0;
        }
        
        return acc;
      }, { totalCostSavings: 0, totalViolationsPrevented: 0 });

      // Calculate client satisfaction score (percentage of 4-5 star reviews)
      const satisfiedClients = (ratingDistribution?.[5] + ratingDistribution?.[4]);
      const satisfactionScore = totalReviews > 0
        ? (satisfiedClients / totalReviews) * 100
        : 0;

      return {
        totalReviews,
        averageRating: parseFloat(averageRating?.toFixed(1)),
        satisfactionScore: parseFloat(satisfactionScore?.toFixed(1)),
        ratingDistribution,
        totalCostSavingsKina: aggregateMetrics?.totalCostSavings,
        totalViolationsPrevented: aggregateMetrics?.totalViolationsPrevented,
        verifiedReviews: reviews?.filter(r => r?.is_verified)?.length || 0
      };
    } catch (error) {
      console.error('Error fetching review analytics:', error);
      throw error;
    }
  },

  // Submit a new review
  async submitReview(reviewData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('client_reviews')
        ?.insert({
          company_id: reviewData?.companyId,
          user_email: user?.email,
          star_rating: reviewData?.starRating,
          review_text: reviewData?.reviewText,
          compliance_success_metrics: reviewData?.complianceSuccessMetrics,
          system_usage_period_days: reviewData?.systemUsagePeriodDays,
          is_verified: false,
          is_published: true
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        companyId: data?.company_id,
        userEmail: data?.user_email,
        starRating: data?.star_rating,
        reviewText: data?.review_text,
        complianceSuccessMetrics: data?.compliance_success_metrics,
        systemUsagePeriodDays: data?.system_usage_period_days,
        isVerified: data?.is_verified,
        isPublished: data?.is_published,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  },

  // Update a review
  async updateReview(reviewId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('client_reviews')
        ?.update({
          star_rating: updates?.starRating,
          review_text: updates?.reviewText,
          compliance_success_metrics: updates?.complianceSuccessMetrics,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', reviewId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        starRating: data?.star_rating,
        reviewText: data?.review_text,
        complianceSuccessMetrics: data?.compliance_success_metrics,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating review:', error);
      throw error;
    }
  },

  // Generate AI case study from review
  async generateCaseStudy(reviewId) {
    try {
      // Import dynamically to avoid circular dependencies
      const { aiCaseStudyService } = await import('./aiCaseStudyService');
      return await aiCaseStudyService?.transformReviewToCaseStudy(reviewId);
    } catch (error) {
      console.error('Error generating case study:', error);
      throw error;
    }
  },

  // Get case study for a review
  async getCaseStudy(reviewId) {
    try {
      const { data, error } = await supabase
        ?.from('client_reviews')
        ?.select('case_study_content, case_study_generated_at')
        ?.eq('id', reviewId)
        ?.single();

      if (error) throw error;

      return {
        caseStudy: data?.case_study_content,
        generatedAt: data?.case_study_generated_at
      };
    } catch (error) {
      console.error('Error fetching case study:', error);
      throw error;
    }
  },

  // Approve review and auto-generate + publish case study
  async approveReviewWithAutoPublish(reviewId, approvalNotes, approvedBy) {
    try {
      // 1. Generate case study using AI
      const { aiCaseStudyService } = await import('./aiCaseStudyService');
      const caseStudyResult = await aiCaseStudyService?.transformReviewToCaseStudy(reviewId);

      if (!caseStudyResult?.success) {
        throw new Error('Failed to generate case study');
      }

      // 2. Approve review (trigger will auto-publish)
      const { data, error } = await supabase
        ?.from('client_reviews')
        ?.update({
          review_status: 'approved',
          reviewed_by: approvedBy,
          reviewed_at: new Date()?.toISOString(),
          approval_notes: approvalNotes,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', reviewId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        success: true,
        review: {
          id: data?.id,
          reviewStatus: data?.review_status,
          autoPublished: data?.auto_published,
          autoPublishedAt: data?.auto_published_at,
          publishedAt: data?.published_at,
          caseStudyContent: data?.case_study_content,
          caseStudyGeneratedAt: data?.case_study_generated_at
        },
        message: 'Review approved and case study auto-published to showcase'
      };
    } catch (error) {
      console.error('Error approving review with auto-publish:', error);
      throw error;
    }
  },

  // Reject review
  async rejectReview(reviewId, rejectionReason, reviewedBy) {
    try {
      const { data, error } = await supabase
        ?.from('client_reviews')
        ?.update({
          review_status: 'rejected',
          reviewed_by: reviewedBy,
          reviewed_at: new Date()?.toISOString(),
          rejection_reason: rejectionReason,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', reviewId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        reviewStatus: data?.review_status,
        rejectionReason: data?.rejection_reason,
        reviewedBy: data?.reviewed_by,
        reviewedAt: data?.reviewed_at
      };
    } catch (error) {
      console.error('Error rejecting review:', error);
      throw error;
    }
  },

  // Get all reviews for moderation (submitted status)
  async getReviewsForModeration() {
    try {
      const { data, error } = await supabase
        ?.from('client_reviews')
        ?.select(`
          *,
          company_settings (
            company_name,
            industry
          )
        `)
        ?.eq('review_status', 'submitted')
        ?.order('submitted_at', { ascending: true });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        companyId: row?.company_id,
        companyName: row?.company_settings?.company_name,
        industry: row?.company_settings?.industry,
        reviewerName: row?.reviewer_name,
        reviewerEmail: row?.reviewer_email,
        reviewerPosition: row?.reviewer_position,
        starRating: row?.star_rating,
        reviewText: row?.review_text,
        reviewStatus: row?.review_status,
        costSavingsKina: row?.cost_savings_kina,
        timeEfficiencyGainPercentage: row?.time_efficiency_gain_percentage,
        regulatoryAdherenceScore: row?.regulatory_adherence_score,
        complianceMetrics: row?.compliance_metrics,
        submittedAt: row?.submitted_at,
        caseStudyContent: row?.case_study_content,
        caseStudyGeneratedAt: row?.case_study_generated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching reviews for moderation:', error);
      throw error;
    }
  },

  // Get published reviews with case studies for showcase
  async getPublishedReviews() {
    try {
      const { data, error } = await supabase
        ?.from('client_reviews')
        ?.select(`
          *,
          company_settings (
            company_name,
            industry,
            organization_type
          )
        `)
        ?.eq('review_status', 'published')
        ?.order('published_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        companyId: row?.company_id,
        companyName: row?.company_settings?.company_name,
        industry: row?.company_settings?.industry,
        organizationType: row?.company_settings?.organization_type,
        reviewerName: row?.reviewer_name,
        reviewerEmail: row?.reviewer_email,
        reviewerPosition: row?.reviewer_position,
        starRating: row?.star_rating,
        reviewText: row?.review_text,
        testimonialType: row?.testimonial_type,
        reviewStatus: row?.review_status,
        isVerified: row?.is_verified,
        isFeatured: row?.is_featured,
        costSavingsKina: row?.cost_savings_kina,
        timeEfficiencyGainPercentage: row?.time_efficiency_gain_percentage,
        regulatoryAdherenceScore: row?.regulatory_adherence_score,
        complianceMetrics: row?.compliance_metrics,
        beforeMetrics: row?.before_metrics,
        afterMetrics: row?.after_metrics,
        departmentType: row?.department_type,
        organizationSize: row?.organization_size,
        useCase: row?.use_case,
        photoUrl: row?.photo_url,
        videoUrl: row?.video_url,
        videoThumbnailUrl: row?.video_thumbnail_url,
        videoDuration: row?.video_duration,
        publishedAt: row?.published_at,
        autoPublished: row?.auto_published,
        autoPublishedAt: row?.auto_published_at,
        caseStudyContent: row?.case_study_content,
        caseStudyGeneratedAt: row?.case_study_generated_at,
        tags: row?.tags,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching published reviews:', error);
      throw error;
    }
  }
};

export default clientReviewService;